/**
 * AgentChatWindow - A versatile chat window for interacting with agents
 *
 * This component provides a chat interface that can be used in multiple contexts:
 * - Floating window: minimizable, closable chat window (default)
 * - Modal/Panel: embedded readonly view for A2A conversations
 *
 * Props:
 * - session: Chat session object with messages array
 * - windowState: Window state (isMinimized, agentName, agentType)
 * - onToggleMinimize: Callback for minimize toggle
 * - onClose: Callback for close action
 * - onSendMessage: Callback for sending messages
 * - isLoading: Loading state indicator
 * - readonly: Hide input section (for viewing conversations)
 * - embedded: Remove floating window chrome (for modal/panel use)
 * - variant: Display variant ('floating' | 'modal' | 'panel')
 * - headerTitle: Custom header title override
 * - headerSubtitle: Optional subtitle (e.g., "Buyers Agent ↔ Sellers Agent")
 * - showHeader: Toggle header visibility (default: true)
 * - messages: Direct messages array (alternative to session.messages)
 * - emptyStateText: Custom empty state message
 * - emptyStateIcon: Custom empty state icon
 * - accentColor: Custom accent color for the header/border
 * - connectionStatus: Optional status indicator ('connecting' | 'connected' | 'disconnected')
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';
import ChatMessage from '../chat/ChatMessage.js';
import AgentActivityFeed from './AgentActivityFeed.js';
import LoadingSpinner from '../LoadingSpinner';
import ChatInput from '../chat/ChatInput.js';
import { getAgentColor, getAgentLabel } from '../../constants/agentTypes.js';

// Add custom scrollbar styles
const getScrollbarStyles = (theme) => `
  .agent-chat-messages::-webkit-scrollbar {
    width: 6px;
  }

  .agent-chat-messages::-webkit-scrollbar-track {
    background: ${theme.scrollbar.trackSmall || 'transparent'};
  }

  .agent-chat-messages::-webkit-scrollbar-thumb {
    background: ${theme.scrollbar.thumb || '#555'};
    border-radius: 3px;
  }

  .agent-chat-messages::-webkit-scrollbar-thumb:hover {
    background: ${theme.scrollbar.thumbHover || '#777'};
  }

  .agent-chat-messages {
    scrollbar-width: thin;
    scrollbar-color: ${theme.scrollbar.thumb || '#555'} ${theme.scrollbar.trackSmall || 'transparent'};
  }

  @keyframes pulse {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }

  @keyframes statusPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

// Base styles - will be modified based on variant
const getStyles = (theme, variant = 'floating') => {
  const isFloating = variant === 'floating';
  const isModal = variant === 'modal';
  const isPanel = variant === 'panel';

  return {
    container: {
      width: isFloating ? '650px' : '100%',
      height: isFloating ? '600px' : '100%',
      backgroundColor: theme.colors.background.sidebar,
      borderRadius: isFloating ? '12px 12px 0 0' : isModal ? theme.borderRadius.lg : '0',
      boxShadow: isFloating ? '0 -4px 20px rgba(0, 0, 0, 0.3)' : 'none',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderBottom: 'none',
    },
    containerMinimized: {
      height: '44px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 12px',
      backgroundColor: theme.colors.primary,
      cursor: isFloating ? 'pointer' : 'default',
      minHeight: '44px',
      boxSizing: 'border-box',
    },
    headerEmbedded: {
      backgroundColor: theme.colors.background.sidebar,
      borderBottom: `1px solid ${theme.colors.border}`,
      cursor: 'default',
    },
    headerLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      flex: 1,
      minWidth: 0,
    },
    headerTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    agentIcon: {
      fontSize: '18px',
    },
    headerTitle: {
      fontSize: theme.fontSize.md,
      fontWeight: theme.fontWeight.semibold,
      color: theme.colors.text.primary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    headerSubtitle: {
      fontSize: theme.fontSize.sm,
      color: theme.colors.text.secondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    headerButtons: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    headerButton: {
      background: 'none',
      border: 'none',
      color: theme.colors.text.primary,
      fontSize: '16px',
      cursor: 'pointer',
      padding: '4px 6px',
      borderRadius: '4px',
      transition: 'background-color 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0.8,
    },
    headerButtonHover: {
      background: 'rgba(255, 255, 255, 0.1)',
      opacity: 1,
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '12px',
      backgroundColor: theme.colors.background.main,
    },
    inputContainer: {
      padding: '12px',
      borderTop: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background.sidebar,
    },
    inputWrapper: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '8px',
    },
    textarea: {
      flex: 1,
      minHeight: '36px',
      maxHeight: '100px',
      padding: '12px 16px',
      border: `1px solid ${theme.colors.border}`,
      borderRadius: '6px',
      backgroundColor: theme.colors.background.main,
      color: theme.colors.text.primary,
      fontSize: theme.fontSize.md,
      resize: 'none',
      outline: 'none',
      fontFamily: 'inherit',
      lineHeight: '1.4',
    },
    sendButton: {
      padding: '22px 18px',
      backgroundColor: theme.colors.primary,
      color: theme.colors.text.primary,
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: theme.fontSize.md,
      fontWeight: theme.fontWeight.medium,
      transition: 'background-color 0.2s ease, opacity 0.2s ease',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    sendButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    loadingIndicator: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px',
      color: theme.colors.text.secondary,
      fontSize: theme.fontSize.sm,
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '24px',
      color: theme.colors.text.secondary,
      textAlign: 'center',
    },
    emptyStateIcon: {
      fontSize: '48px',
      marginBottom: '12px',
      opacity: 0.5,
    },
    emptyStateText: {
      fontSize: theme.fontSize.md,
    },
    unreadBadge: {
      backgroundColor: theme.colors.danger || theme.colors.danger,
      color: '#fff',
      fontSize: '10px',
      fontWeight: theme.fontWeight.semibold,
      padding: '2px 6px',
      borderRadius: '10px',
      marginLeft: '4px',
    },
    connectionStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: theme.fontSize.xs,
      padding: '4px 8px',
      borderRadius: theme.borderRadius.sm,
      marginLeft: '8px',
    },
    connectionStatusConnected: {
      backgroundColor: 'rgba(76, 175, 80, 0.15)',
      color: theme.colors.connection.connectedText,
    },
    connectionStatusConnecting: {
      backgroundColor: 'rgba(255, 193, 7, 0.15)',
      color: theme.colors.connection.connectingText,
      animation: 'statusPulse 1.5s ease-in-out infinite',
    },
    connectionStatusDisconnected: {
      backgroundColor: 'rgba(244, 67, 54, 0.15)',
      color: theme.colors.connection.disconnectedText,
    },
    statusDot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: 'currentColor',
    },
  };
};

const AgentChatWindow = ({
  // Core props
  session,
  windowState,
  onToggleMinimize,
  onClose,
  onSendMessage,
  isLoading,
  loadingStatus, // String status while session is being created (e.g. "Creating session...")
  // New props for readonly/embedded modes
  readonly = false,
  embedded = false,
  variant = 'floating', // 'floating' | 'modal' | 'panel'
  headerTitle,
  headerSubtitle,
  showHeader = true,
  messages: directMessages,
  emptyStateText: customEmptyText,
  emptyStateIcon: customEmptyIcon,
  accentColor,
  connectionStatus, // 'connecting' | 'connected' | 'disconnected'
  style: customStyle,
}) => {
  const { theme } = useAppTheme();

  const [inputValue, setInputValue] = useState('');
  const [hoveredButton, setHoveredButton] = useState(null);
  const messagesEndRef = useRef(null);

  // Determine effective variant - embedded implies non-floating
  const effectiveVariant = embedded ? (variant === 'floating' ? 'modal' : variant) : variant;
  const styles = useMemo(() => getStyles(theme, effectiveVariant), [theme, effectiveVariant]);
  const isFloating = effectiveVariant === 'floating';

  // Inject scrollbar styles
  useEffect(() => {
    const styleId = 'agent-chat-scrollbar-styles';
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = getScrollbarStyles(theme);
  }, [theme]);

  // Get messages from either direct prop or session
  const displayMessages = directMessages || session?.messages || [];

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && !windowState?.isMinimized) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [displayMessages, windowState?.isMinimized]);

  const handleSendMessage = async (message, attachments = []) => {
    if (!message && attachments.length === 0) return;
    if (isLoading || readonly) return;

    setInputValue('');

    if (onSendMessage) {
      await onSendMessage(session?.id, message, attachments);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleHeaderClick = (e) => {
    // Don't toggle if clicking on buttons or not floating
    if (e.target.tagName === 'BUTTON' || !isFloating) return;
    onToggleMinimize?.();
  };

  const isMinimized = windowState?.isMinimized;
  const agentName = headerTitle || windowState?.agentName || session?.name || 'Agent Chat';
  const agentType = windowState?.agentType || session?.agentType;
  const headerColor = accentColor || getAgentColor(agentType);

  // Determine empty state content
  const emptyIcon = customEmptyIcon || '💬';
  const emptyText =
    customEmptyText || (readonly ? 'No messages in this conversation yet' : 'Start a conversation with this agent');

  // Connection status indicator
  const renderConnectionStatus = () => {
    if (!connectionStatus) return null;

    const statusStyles = {
      connecting: styles.connectionStatusConnecting,
      connected: styles.connectionStatusConnected,
      disconnected: styles.connectionStatusDisconnected,
    };

    const statusLabels = {
      connecting: 'Connecting...',
      connected: 'Live',
      disconnected: 'Disconnected',
    };

    return (
      <div style={{ ...styles.connectionStatus, ...statusStyles[connectionStatus] }}>
        <div style={styles.statusDot} />
        <span>{statusLabels[connectionStatus]}</span>
      </div>
    );
  };

  return (
    <div
      style={{
        ...styles.container,
        ...(isFloating ? { borderLeft: `4px solid ${headerColor}` } : {}),
        ...(isMinimized ? styles.containerMinimized : {}),
        ...customStyle,
      }}
    >
      {/* Header */}
      {showHeader && (
        <div
          style={{
            ...styles.header,
            ...(embedded ? styles.headerEmbedded : {}),
            ...(!embedded && !isFloating ? { borderLeft: `4px solid ${headerColor}` } : {}),
          }}
          onClick={handleHeaderClick}
        >
          <div style={styles.headerLeft}>
            <div style={styles.headerTitleRow}>
              <span style={styles.headerTitle} title={agentName}>
                {agentName}
              </span>
              {renderConnectionStatus()}
            </div>
            {headerSubtitle && (
              <span style={styles.headerSubtitle} title={headerSubtitle}>
                {headerSubtitle}
              </span>
            )}
          </div>
          {/* Only show header buttons for floating variant without embedded mode */}
          {isFloating && !embedded && (
            <div style={styles.headerButtons}>
              <button
                style={{
                  ...styles.headerButton,
                  ...(hoveredButton === 'minimize' ? styles.headerButtonHover : {}),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMinimize?.();
                }}
                onMouseEnter={() => setHoveredButton('minimize')}
                onMouseLeave={() => setHoveredButton(null)}
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                {isMinimized ? '▲' : '▼'}
              </button>
              <button
                style={{
                  ...styles.headerButton,
                  ...(hoveredButton === 'close' ? styles.headerButtonHover : {}),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose?.();
                }}
                onMouseEnter={() => setHoveredButton('close')}
                onMouseLeave={() => setHoveredButton(null)}
                title="End chat session"
              >
                ✕
              </button>
            </div>
          )}
          {/* For embedded/modal mode, show close button if onClose provided */}
          {(embedded || !isFloating) && onClose && (
            <div style={styles.headerButtons}>
              <button
                style={{
                  ...styles.headerButton,
                  ...(hoveredButton === 'close' ? styles.headerButtonHover : {}),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose?.();
                }}
                onMouseEnter={() => setHoveredButton('close')}
                onMouseLeave={() => setHoveredButton(null)}
                title="Close"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      {/* Chat body - only shown when not minimized */}
      {!isMinimized && (
        <>
          {/* Messages */}
          <div style={styles.messagesContainer} className="agent-chat-messages">
            {loadingStatus ? (
              <div style={styles.emptyState}>
                <LoadingSpinner size="small" message={loadingStatus} />
              </div>
            ) : displayMessages.length > 0 ? (
              displayMessages.map((message, index) => (
                <ChatMessage key={message.id || index} message={message} formatTime={formatTime} />
              ))
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyStateIcon}>{emptyIcon}</div>
                <div style={styles.emptyStateText}>{emptyText}</div>
              </div>
            )}
            {isLoading && !loadingStatus && !displayMessages.some((m) => m.isStreaming) && (
              <div style={{ padding: '0 4px' }}>
                <LoadingSpinner size="small" message="Thinking..." />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input - only show if not readonly */}
          {!readonly && (
            <div style={styles.inputContainer}>
              <ChatInput
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSendMessage}
                isLoading={isLoading || !!loadingStatus}
                placeholder="Type your message..."
                maxTextareaHeight={100}
                textareaStyle={styles.textarea}
                sendButtonStyle={styles.sendButton}
                sendButtonDisabledStyle={styles.sendButtonDisabled}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgentChatWindow;
